// https://developers.google.com/search/docs/appearance/structured-data/product
// use https://search.google.com/test/rich-results to test
export type MetaData = {
  title: string;
  description: string;
  keywords: string;
  "og:title": string;
  "og:description": string;
  "og:type": string;
  "og:image": string;
  "og:url": string;
  "twitter:card": string;
  "twitter:title": string;
  "twitter:description": string;
  "twitter:image": string;
  "twitter:image:alt": string;
};

export type StructuredData = {
  "@context": "https://schema.org";
  "@type": "Product";
  name: string;
  description: string;
  image: string[];
  brand: {
    "@type": "Brand";
    name: string;
  };
  offers: {
    "@type": "Offer";
    price: string;
    priceCurrency: string;
    availability: "InStock" | "OutOfStock" | "PreOrder";
    priceValidUntil: string;
    url: string;
    applicableCountry: string[];
    unitCode: string;
    seller: {
      "@type": "Organization";
      name: string;
    };
    shippingDetails: {
      "@type": "OfferShippingDetails";
      shippingRate: {
        "@type": "MonetaryAmount";
        value: "0";
        currency: "USD";
      };
      shippingDestination: {
        "@type": "DefinedRegion";
        addressCountry: string[];
      };
      deliveryTime: {
        "@type": "ShippingDeliveryTime";
        handlingTime: {
          "@type": "QuantitativeValue";
          minValue: "0";
          maxValue: "0";
        };
      };
    };
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy";
      returnPolicyCategory: "MerchantReturnFiniteReturnWindow";
      merchantReturnDays: "14";
      returnFees: "FreeReturn";
      applicableCountry: string[];
    };
  };
  //   aggregateRating: {
  //     "@type": "AggregateRating";
  //     ratingValue: string;
  //     reviewCount: string;
  //     bestRating: string;
  //   };
  //   review: Array<{
  //     "@type": "Review";
  //     reviewRating: {
  //       "@type": "Rating";
  //       ratingValue: string;
  //       bestRating: string;
  //     };
  //     author: {
  //       "@type": "Person";
  //       name: string;
  //     };
  //     reviewBody: string;
  //   }>;
  additionalProperty: Array<{
    "@type": "PropertyValue";
    name: string;
    value: string;
  }>;
};

export const PRODUCT_FEATURES = [
  "500+ up-to-date CLF-C02 practice questions",
  "Detailed explanations for every answer",
  "Unlimited practice exam simulations",
  "Performance tracking dashboard",
  "Mobile-friendly",
  "14-day money-back guarantee",
  "One-time payment, no subscriptions",
] as const;

export function generateMetaData(): MetaData {
  const url = "https://cloudexamhub.com";
  return {
    title: "AWS Cloud Practitioner Practice Exams | Cloud Exam Hub",
    description:
      "Master the AWS Certified Cloud Practitioner CLF-C02 exam with 500+ up-to-date practice questions, detailed explanations, and unlimited test simulations. One-time payment, 14-day guarantee.",
    keywords:
      "AWS Cloud Practitioner practice exam, CLF-C02 practice test, AWS certification prep, cloud computing certification, AWS exam questions, AWS practice questions 2026",
    "og:title": "AWS Cloud Practitioner Practice Exams | Cloud Exam Hub",
    "og:description":
      "500+ up-to-date CLF-C02 practice questions with detailed explanations. Unlimited exam simulations. One-time payment of $11.99.",
    "og:type": "website",
    "og:image": `${url}/cloud-exam-hub-full-logo-white-background.png`,
    "og:url": url,
    "twitter:card": "summary_large_image",
    "twitter:title": "AWS Cloud Practitioner Practice Exams | Cloud Exam Hub",
    "twitter:description":
      "500+ up-to-date CLF-C02 practice questions with detailed explanations. Unlimited exam simulations. One-time payment of $11.99.",
    "twitter:image": `${url}/cloud-exam-hub-full-logo-white-background.png`,
    "twitter:image:alt": "Cloud Exam Hub - AWS Cloud Practitioner Practice Exams",
  };
}

export function generateStructuredData(): StructuredData {
  const currentDate = new Date();
  const oneYearFromNow = new Date(
    currentDate.setFullYear(currentDate.getFullYear() + 1)
  );
  const baseUrl = "https://cloudexamhub.com";

  // biggest markets for AWS certifications
  const availableCountries = [
    "US", // United States - Largest tech market, AWS headquarters
    "CA", // Canada - Major English-speaking tech market
    "GB", // United Kingdom - Major European tech hub
    "AU", // Australia - Strong cloud adoption, English-speaking
    "DE", // Germany - Largest European economy
    "IN", // India - Large English-speaking tech workforce
    "JP", // Japan - Major Asian tech market
    "SG", // Singapore - Asian tech hub, business English
    "NL", // Netherlands - European tech hub, high English proficiency
    "FR", // France - Large European market
    "IL", // Israel - Strong tech market, English-speaking
    "BR", // Brazil - Largest South American market, Portuguese-speaking
    "MX", // Mexico - Largest Spanish-speaking market
    "AR", // Argentina - Large Spanish-speaking market
    "CO", // Colombia - Large Spanish-speaking market
    "CL", // Chile - Large Spanish-speaking market
    "PE", // Peru - Large Spanish-speaking market
  ];

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "AWS Certified Cloud Practitioner Practice Exams",
    description:
      "Comprehensive practice exams with 500+ up-to-date questions for AWS Certified Cloud Practitioner certification (CLF-C02)",
    image: [`${baseUrl}/cloud-check.png`],
    brand: {
      "@type": "Brand",
      name: "Cloud Exam Hub",
    },
    offers: {
      "@type": "Offer",
      price: "11.99",
      priceCurrency: "USD",
      availability: "InStock",
      priceValidUntil: oneYearFromNow.toISOString().split("T")[0],
      url: baseUrl,
      applicableCountry: availableCountries,
      unitCode: "C62", // "one" - for digital products
      seller: {
        "@type": "Organization",
        name: "Cloud Exam Hub",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: "0",
          currency: "USD",
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: availableCountries,
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: "0",
            maxValue: "0",
          },
        },
      },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        returnPolicyCategory: "MerchantReturnFiniteReturnWindow",
        merchantReturnDays: "14",
        returnFees: "FreeReturn",
        applicableCountry: availableCountries,
      },
    },
    additionalProperty: [
      {
        "@type": "PropertyValue",
        name: "Question Bank",
        value: PRODUCT_FEATURES[0],
      },
      {
        "@type": "PropertyValue",
        name: "Explanations",
        value: PRODUCT_FEATURES[1],
      },
      {
        "@type": "PropertyValue",
        name: "Practice Tests",
        value: PRODUCT_FEATURES[2],
      },
      {
        "@type": "PropertyValue",
        name: "Analytics",
        value: PRODUCT_FEATURES[3],
      },
      {
        "@type": "PropertyValue",
        name: "Accessibility",
        value: PRODUCT_FEATURES[4],
      },
      {
        "@type": "PropertyValue",
        name: "Guarantee",
        value: PRODUCT_FEATURES[5],
      },
      {
        "@type": "PropertyValue",
        name: "Pricing",
        value: PRODUCT_FEATURES[6],
      },
    ],
  };
}
